import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { Button, Divider, Grid, Stack, Typography } from '@mui/material';

import { FormRoot, RenderFormField, SideModal } from '@/components';
import { withLoader, WithLoaderProps } from '@/hocs';
import { IPriorityForm } from '@/models';
import { useBuildingInspectionContext } from '@/pages/Tools/BuildingInspection/BuildingInspectionContext';
import { generateRandomId } from '@/pages/Tools/BuildingInspection/dummy_data/dummy_helper';
import { violationsFormFields } from '@/pages/Tools/BuildingInspection/dummy_data/dummy_violations';
import { IViolation } from '@/pages/Tools/BuildingInspection/Models';
import { useToastStore } from '@/store';

interface IViolationModalProps extends WithLoaderProps {
  openModal: string | undefined;
  setOpenModal: (selectedItemId: string | undefined) => void;
}
const ViolationModal = ({ openModal, setOpenModal }: IViolationModalProps) => {
  const { inspectionId } = useParams();
  const { updateToast } = useToastStore();
  const { setViolations } = useBuildingInspectionContext();

  const methods = useForm<IPriorityForm>({
    defaultValues: {},
    mode: 'all',
  });

  const { handleSubmit, reset } = methods;

  useEffect(() => {
    // If modal is opened, it means user wants to mark the Case as Priority
    if (openModal) {
      reset();
    }
  }, [openModal, reset]);

  const onSubmit = async (values: IPriorityForm) => {
    setViolations((violations: IViolation[]) => [
      ...violations,
      {
        _id: generateRandomId(),
        inspection_id: inspectionId,
        issueDate: new Date().toLocaleDateString(),
        ...values,
      } as unknown as IViolation,
    ]);
    setOpenModal(undefined);
    if (!inspectionId) {
      updateToast({
        message: 'invalid case id ',
        open: true,
      });
      return;
    }
  };

  return (
    <SideModal open={!!openModal} onClose={() => setOpenModal(undefined)}>
      <Stack
        sx={{
          bgcolor: 'white',
          height: '100%',
          p: 2,
          '.priorityComment textarea': {
            minHeight: 100,
          },
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1.5 }}>
          Add violation
        </Typography>
        <Divider />

        <FormProvider {...methods}>
          <FormRoot onSubmit={handleSubmit(onSubmit)} sx={{ height: '100%' }}>
            <Grid container spacing={2}>
              {violationsFormFields.map((field) => (
                <Grid key={field?.name} {...field?.grid} item>
                  <RenderFormField {...field} />
                </Grid>
              ))}
            </Grid>
            <Stack
              flexDirection="row"
              justifyContent="flex-end"
              alignSelf="flex-end"
              width="100%"
            >
              <Button
                variant="contained"
                color="inherit"
                size="medium"
                sx={{ mr: 2 }}
                onClick={() => setOpenModal(undefined)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                size="medium"
                onClick={handleSubmit(onSubmit)}
              >
                Save
              </Button>
            </Stack>
          </FormRoot>
        </FormProvider>
      </Stack>
    </SideModal>
  );
};
export default withLoader(ViolationModal);
